'use client'

import type { ComponentProps, ReactElement } from 'react'

import { cloneElement, useCallback, useEffect, useRef, useState } from 'react'

import { Tooltip, TooltipContent, TooltipTrigger } from '../_base/Tooltip'

interface TruncatedTooltipProps {
  /** Full text; revealed in a tooltip only when the rendered element is visually clipped. */
  text: string
  /**
   * Renders the truncating element. Attach the provided ref and a `truncate`
   * class to it — the ref is a callback ref so it re-measures on mount, remount
   * (e.g. when this component wraps it in a tooltip), and element resize.
   * `isTruncated` is true while the element is clipped and acting as the tooltip trigger.
   */
  children: (
    ref: (element: HTMLElement | null) => void,
    isTruncated: boolean
  ) => ReactElement<{ tabIndex?: number }>
  /** Side the reveal tooltip opens on. Defaults to `top`. */
  side?: ComponentProps<typeof TooltipContent>['side']
}

/**
 * Wraps a single-line, ellipsis-truncated element and reveals its full text in a
 * tooltip only while the element is actually clipped (`scrollWidth` exceeds
 * `clientWidth`). Element-agnostic via a render prop so it works for `Text`,
 * `Heading`, `BreadcrumbLink`, etc. Relies on the app-level `TooltipProvider`.
 */
export function TruncatedTooltip({ text, children, side = 'top' }: TruncatedTooltipProps) {
  const [isTruncated, setIsTruncated] = useState(false)
  const elementRef = useRef<HTMLElement | null>(null)
  const observerRef = useRef<ResizeObserver | null>(null)

  // 1px tolerance avoids sub-pixel false positives (font/zoom/DPI rounding).
  const measure = useCallback(() => {
    const element = elementRef.current
    if (element) setIsTruncated(element.scrollWidth - element.clientWidth > 1)
  }, [])

  const ref = useCallback(
    (element: HTMLElement | null) => {
      observerRef.current?.disconnect()
      elementRef.current = element
      if (!element) return
      measure()
      observerRef.current = new ResizeObserver(measure)
      observerRef.current.observe(element)
    },
    [measure]
  )

  // Re-measure when the text changes in place (same element, new content, no
  // resize) — e.g. a placeholder name resolving to the longer on-chain name.
  useEffect(() => {
    measure()
  }, [text, measure])

  const element = children(ref, isTruncated)

  if (!isTruncated) return element

  // Make the trigger keyboard-focusable when truncated so the reveal tooltip is
  // reachable without a pointer, even for non-interactive elements (`Heading`,
  // `Text`). Respect an explicit `tabIndex` if the consumer already set one.
  const trigger = cloneElement(element, { tabIndex: element.props.tabIndex ?? 0 })

  return (
    <Tooltip>
      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      <TooltipContent side={side}>{text}</TooltipContent>
    </Tooltip>
  )
}
